/* CanvaAdminPanel — Canva cover order history, revenue, and cost-vs-revenue chart.
 * Admin finance tab: GET /api/admin/canva/dashboard
 */
import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { canvaApi } from '@/lib/api';
import { Loader2, TrendingUp, ShoppingBag, DollarSign, BarChart3, Download } from 'lucide-react';

// ── small helpers ──────────────────────────────────────────────────────────────

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-sm border border-border bg-card px-4 py-3 flex items-start gap-3">
      <div className="mt-0.5 rounded-sm bg-primary/10 p-2">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

function fmt(n) { return `$${Number(n ?? 0).toFixed(2)}`; }
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
}

// ── component ─────────────────────────────────────────────────────────────────

export default function CanvaAdminPanel() {
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState(null);
  const [busy, setBusy]   = useState(false);

  useEffect(() => {
    setBusy(true);
    canvaApi.adminDashboard()
      .then(d => { setData(d); setErr(null); })
      .catch(e => setErr(e?.response?.data?.detail || 'Failed to load dashboard.'))
      .finally(() => setBusy(false));
  }, []);

  if (busy) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (err) {
    return (
      <div className="p-4 text-sm text-rose-600">{err}</div>
    );
  }
  if (!data) return null;

  const { summary, chart, orders } = data;

  const downloadCSV = () => {
    const header = 'Date,User ID,Amount ($),Stripe Fee ($),Net ($)';
    const rows = orders.map(o =>
      [
        (o.created_at || '').slice(0, 10),
        o.user_id || '',
        Number(o.amount).toFixed(2),
        Number(o.stripe_fee).toFixed(2),
        Number(o.net).toFixed(2),
      ].join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `canva-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 p-4" data-testid="canva-admin-panel">

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat icon={ShoppingBag}  label="Total Orders"   value={summary.total_orders} />
        <Stat icon={DollarSign}   label="Total Revenue"  value={fmt(summary.total_revenue)} sub={`avg ${fmt(summary.avg_price_usd)} / cover`} />
        <Stat icon={BarChart3}    label="Stripe Fees"    value={fmt(summary.total_stripe_fees)} sub="2.9% + $0.30 / txn" />
        <Stat icon={TrendingUp}   label="Net Revenue"    value={fmt(summary.net_revenue)} sub={`${summary.margin_pct}% margin`} />
      </div>

      {/* Cost-vs-Revenue chart */}
      {chart.length > 0 && (
        <div className="rounded-sm border border-border bg-card p-4" data-testid="canva-chart">
          <h3 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            Revenue vs Stripe COGS — last {chart.length} days with activity
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={d => d.slice(5)} /* MM-DD */
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={v => `$${v}`}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}
                formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name === 'revenue' ? 'Revenue' : 'Stripe fees']}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue" name="Revenue"     fill="hsl(var(--primary))"      radius={[3,3,0,0]} />
              <Bar dataKey="cogs"    name="Stripe fees" fill="hsl(var(--muted-foreground)/0.5)" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Order history table */}
      <div className="rounded-sm border border-border bg-card" data-testid="canva-orders-table">
        <div className="border-b border-border px-4 py-2.5 flex items-center justify-between">
          <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
            Order History
          </h3>
          <span className="text-[11px] text-muted-foreground">{orders.length} record{orders.length !== 1 ? 's' : ''}</span>
          <button
            onClick={downloadCSV}
            disabled={orders.length === 0}
            data-testid="canva-csv-download"
            className="ml-2 flex items-center gap-1 rounded-sm border border-border bg-secondary/50 px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-secondary disabled:opacity-40"
            title="Download orders as CSV"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>
        {orders.length === 0 ? (
          <p className="p-4 text-[12px] text-muted-foreground">No paid cover orders yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-border bg-secondary/30 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Stripe Fee</th>
                  <th className="px-3 py-2 text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => (
                  <tr
                    key={o.session_id || i}
                    className="border-b border-border/50 hover:bg-secondary/20 transition-colors"
                    data-testid={`canva-order-row-${i}`}
                  >
                    <td className="px-3 py-1.5 text-muted-foreground">{fmtDate(o.created_at)}</td>
                    <td className="px-3 py-1.5 font-mono text-[10.5px] text-muted-foreground">
                      {(o.user_id || '').slice(0, 18)}…
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(o.amount)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-rose-600">{fmt(o.stripe_fee)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-emerald-600 font-semibold">{fmt(o.net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-secondary/20 font-semibold">
                  <td colSpan={2} className="px-3 py-2 text-[11px] text-muted-foreground">Totals</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(summary.total_revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-600">{fmt(summary.total_stripe_fees)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{fmt(summary.net_revenue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
